export interface CreateInsertionOrderRequest {
  budget: string[][]; // Array of arrays of strings - insertion order table structure
  advertiser_name: string;
  campaign_name: string;
  start_date: string; // ISO date string (YYYY-MM-DD)
  end_date: string; // ISO date string (YYYY-MM-DD)
  company_name: string | null;
  client_email: string;
  client_name: string;
  agent_email: string;
  agent_name: string;
  cc_emails: string; // Comma-separated email addresses
  logo: string; // 'campaign_insights' or 'invisibly'
  prebill: boolean;
  include_sla: boolean;
}

export interface Company {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  ext_company_id: string;
  emails: string;
  company_name: string;
  parent_company_name: string | null;
  balance: string;
  terms: string;
  sublevel: string;
}

export interface Terms {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  external_term_id: string;
  name: string;
  day_of_month_due: number | null;
  number_of_days_till_due: number | null;
}

export interface SKU {
  id: string;
  created_at: string;
  updated_at: string;
  status: string | null;
  external_sku_id: string | null;
  sku: string | null;
  name: string | null;
  keywords: string | null;
  bill_type: string | null;
  qty_is_one: boolean;
}

/** PATCH b2b/billing/skus/:id — editable fields only (id and timestamps are read-only). */
export type SKUPatchPayload = Partial<Pick<SKU, 'status' | 'external_sku_id' | 'sku' | 'name' | 'keywords' | 'bill_type' | 'qty_is_one'>>;

export interface InsertionOrder extends CreateInsertionOrderRequest {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  insertion_order_group_id: string;
  ext_insertion_order_id: string;
  client_id: string;
  agent_id: string;
  document_name: string | null;
  document_url: string | null;
  docusign_url: string | null;
  envelope_id: string | null;
  is_active: boolean;
  client_delivered: boolean;
  client_signed: boolean;
  agency_delivered: boolean;
  agency_signed: boolean;
}

export interface InsertionOrderAutocompleteSuggestions {
  advertiser: string[];
  campaign: string[];
  billing_contact: string[]; // Maps to client_name
  billing_contact_email: string[]; // Maps to client_email
  company_name: (string | null)[];
  agent_name: string[];
  agent_email: string[];
}

/** Timing details on invoice group */
export interface InvoiceGroupDetailsTiming {
  started_at?: string;
  completed_at?: string;
}

/** Raw data reference for invoice group */
export interface InvoiceGroupRawData {
  url: string;
  path: string;
  row_count: number;
  uploaded_at: string;
}

/** One row in a validation file's data array */
export interface ValidationFileDataRow {
  sku: string | null;
  spend: number;
  source: string;
  account_id: string | null;
  margin_pct: number | null;
  campaign_id: string | null;
  impressions: number;
}

/** Single file in validations.files */
export interface ValidationFile {
  filename: string;
  rowcount: number;
  data?: ValidationFileDataRow[];
  uploaded_at: string;
  /** Platform/source identifier from the validation upload */
  source?: string;
}

/** One field mismatch from invoice_group.validations.mismatches[].mismatches */
export interface ValidationMismatchItem {
  field: string;
  actual: number;
  expected: number;
}

/** One entry in invoice_group.validations.mismatches (per line item / source) */
export interface ValidationMismatchEntry {
  source: string;
  row_index: number;
  mismatches: ValidationMismatchItem[];
  campaign_id: string;
  line_item_id: string;
}

/** Validations section on invoice group (top-level) */
export interface InvoiceGroupValidations {
  files: ValidationFile[];
  mismatches?: ValidationMismatchEntry[];
}

/** Response item from DELETE .../validate/upload/remove/ */
export interface DeleteInvoiceValidationFileResult {
  removed: { source: string; filename: string };
  remaining_files: number;
}

/** Single row from GET/PATCH invoice_line_item_validations */
export interface InvoiceLineItemValidation {
  id: string;
  source: string;
  field: string;
  value: string | null;
  applied: boolean;
  note: string | null;
  is_missing: boolean;
  details: Record<string, unknown> | null;
  line_item: string;
  /** Often `"active"`; may be omitted on some responses */
  status?: string;
  created_at: string;
  updated_at: string;
}

/** Django REST–style paginated list (if API ever returns this shape) */
export interface InvoiceLineItemValidationsPaginated {
  count: number;
  next: string | null;
  previous: string | null;
  results: InvoiceLineItemValidation[];
}

/** Invisibly paginated list (`items` + page metadata) — typical for GET invoice_line_item_validations */
export interface InvoiceLineItemValidationsPaginatedItems {
  page: number;
  page_size: number;
  total_objects: number;
  total_pages: number;
  items: InvoiceLineItemValidation[];
}

export function normalizeInvoiceLineItemValidationsItem(
  item:
  | InvoiceLineItemValidation[]
  | InvoiceLineItemValidationsPaginated
  | InvoiceLineItemValidationsPaginatedItems
  | unknown
): InvoiceLineItemValidation[] {
  if (Array.isArray(item)) {
    return item;
  }
  if (item && typeof item === 'object' && 'results' in item) {
    const r = (item as InvoiceLineItemValidationsPaginated).results;
    return Array.isArray(r) ? r : [];
  }
  if (item && typeof item === 'object' && 'items' in item) {
    const r = (item as InvoiceLineItemValidationsPaginatedItems).items;
    return Array.isArray(r) ? r : [];
  }
  return [];
}

/** Single error from top-level errors array */
export interface InvoiceGroupDetailsError {
  error: string;
  traceback?: string;
  line_index?: number;
  campaign_id?: string | number;
}

export interface InvoiceGroup {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  month: number;
  year: number;
  invoice_status: string;
  /** Aggregated amount to bill for the group (from API when present) */
  amount_to_bill?: number | null;
  /** Timing for validation/processing */
  details?: InvoiceGroupDetailsTiming | null;
  /** Raw upload reference */
  raw_data?: InvoiceGroupRawData | null;
  /** Validation/processing errors (top-level) */
  errors?: InvoiceGroupDetailsError[];
  /** Validation files (uploaded CSVs and their parsed data) */
  validations?: InvoiceGroupValidations | null;
}

export interface InvoiceGroupDetails extends InvoiceGroup {
  invoices: Invoice[];
  orphan_invoice_line_items: InvoiceLineItem[];
}

export type BillingJobStatusValue =
  | 'pending'
  | 'fetching_data'
  | 'generating_invoices'
  | 'complete'
  | 'failed';

export interface BillingJobStatusResponse {
  billing_month_id: string;
  status: BillingJobStatusValue;
  error_message: string | null;
  started_at: string | null;
  updated_at: string | null;
}

/** Validation mismatch details when attention_needed is true */
export interface InvoiceLineItemDetailsValidations {
  margin_pct?: number | null;
  impressions?: number | null;
  advertiser_spend?: number | null;
}

/** Optional pipeline / normalised primitive when line came from billing ingestion */
export interface NormalizedLineItemPrimitiveBrief {
  description?: string;
  quantity?: string | number;
  unit_price?: string | number;
  suggested_sku_code?: string | null;
  client_name?: string | null;
  client_id?: string | null;
  raw_data?: Record<string, unknown>;
}

export interface InvoiceLineItemDetails {
  impressions?: number;
  advertiser_spend?: number;
  validations?: InvoiceLineItemDetailsValidations;
  pipeline_primitive?: NormalizedLineItemPrimitiveBrief;
  client_name?: string | null;
  client_id?: string | null;
}

export interface InvoiceLineItem {
  id: string;
  created_at: string;
  updated_at: string;
  last_sync: string | null;
  status: string;
  ext_line_item_id: string | null;
  ext_campaign_id: string;
  invoice_line_item_status: string;
  name: string;
  channel: string;
  platform: string;
  description: string | null;
  impressions: number;
  advertiser_spend: number;
  net_spend: number;
  margin: number;
  advertiser_spend_override: number | null;
  margin_pct_override: number | null;
  amount_to_bill: number | null;
  margin_pct: number;
  notes: string | null;
  qty: number;
  sales_tax_code: string;
  do_not_bill: boolean;
  attention_needed: boolean;
  validated: boolean;
  sku: string;
  sku_override: string | null;
  invoice_group: string;
  /** Null when line is staging / not yet assigned to an invoice */
  invoice: string | null;
  /** Set when line originated from a billing import run */
  billing_import?: number | null;
  hide: boolean;
  campaign: string;
  rate: number | null;
  /** When attention_needed is true, contains values that did not match validation */
  details?: InvoiceLineItemDetails | null;
  /** e.g. "unknown" */
  validation_status?: string;
  /** valid | invalid | unknown (or synonyms) — line-level outcome of field validation */
  field_validation_status?: string;
  /** Top-level validations (nullable when not present) */
  validations?: unknown | null;
}

/** Compact filter facets for list rows (no nested line items). */
export interface InvoiceFilterMeta {
  sku_ids: string[];
  platform_ids: string[];
  campaign_ids: string[];
  search_text: string;
}

export interface Invoice {
  id: string;
  /** Populated after lazy load (drawer / expand); empty on initial list fetch. */
  invoice_line_items: InvoiceLineItem[];
  /** List API aggregates (present when line items are not loaded). */
  line_item_count?: number;
  visible_line_item_count?: number;
  channel_display?: string;
  list_impressions?: number;
  has_line_item_notes?: boolean;
  line_items_attention_count?: number;
  filter_meta?: InvoiceFilterMeta;
  created_at: string;
  updated_at: string;
  last_sync: string | null;
  status: string;
  ext_invoice_number: string | null;
  /** External invoice id from upstream (e.g. accounting sync); used when gating download. */
  ext_invoice_id?: string | null;
  needs_sync?: boolean;
  invoice_date: string | null;
  due_date: string | null;
  currency: string;
  description: string | null;
  invoice_status: string;
  attention_needed: boolean;
  hide: boolean;
  validated?: boolean;
  remaining_balance: number | null;
  subtotal: number;
  /** Aggregated amount to bill for this invoice (from API when present) */
  amount_to_bill?: number | null;
  pdf_url: string | null;
  details?: unknown | null;
  terms: string | null;
  company: string;
  invoice_group: string;
  has_balance?: boolean;
  do_not_bill?: boolean;
}

export interface CompanyMapping {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  advertiser_campaign_name: string;
  company: string;
}

export interface CreateCompanyMappingRequest {
  company: string;
  advertiser_campaign_name: string;
}

export interface InvoiceGroupPreviewItem {
  advertiser_name: string;
  campaign_name: string;
  platform: string;
  company: string | null;
}

/** GET b2b/billing/dashboard/needed_actions — account row */
export interface BillingNeededActionAccount {
  id: string;
  entity_type: 'account';
  title: string;
  source: string;
  attention_needed: boolean;
  company_id: string | null;
  company_name: string | null;
  created_at: string;
}

/** GET b2b/billing/dashboard/needed_actions — campaign row */
export interface BillingNeededActionCampaign {
  id: string;
  entity_type: 'campaign';
  title: string;
  platform: string;
  channel: string;
  attention_needed: boolean;
  account_id: string | null;
  account_title: string;
  company_id: string | null;
  company_name: string | null;
  created_at: string;
}

export type BillingNeededActionItem = BillingNeededActionAccount | BillingNeededActionCampaign;

/**
 * Paginated wrapper for GET b2b/billing/dashboard/needed_actions (`Response.item`).
 * Also accepts legacy DRF-style `{ count, next, previous, results }` in the normaliser.
 */
export interface BillingNeededActionsPaginated {
  total_objects: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: BillingNeededActionItem[];
}

/** Legacy DRF pagination shape (still supported by normaliser). */
export interface BillingNeededActionsPaginatedDrf {
  count: number;
  next: string | null;
  previous: string | null;
  results: BillingNeededActionItem[];
}

/** GET b2b/billing/dashboard */
export interface BillingDashboardInvoicedVsPaidPoint {
  month: string;
  invoiced: number;
  paid: number;
}

export interface BillingDashboardCharts {
  invoiced_vs_paid_mom: BillingDashboardInvoicedVsPaidPoint[];
  invoiced_vs_paid_last_3_months: BillingDashboardInvoicedVsPaidPoint[];
}

export interface BillingDashboardMetrics {
  amount_invoiced: number;
  percent_change_invoiced_mom: number | null;
  amount_collected: number;
  outstanding_amount: number;
}

export interface BillingDashboardSnapshot {
  range: {
    start_date: string;
    end_date: string;
  };
  updated_at: string | null;
}

export interface BillingDashboardApiItem {
  charts: BillingDashboardCharts;
  metrics: BillingDashboardMetrics;
  snapshot: BillingDashboardSnapshot;
}

/** Default snapshot when none exists yet or the overview payload is null. */
export const EMPTY_BILLING_DASHBOARD_API_ITEM: BillingDashboardApiItem = {
  charts: {
    invoiced_vs_paid_mom: [],
    invoiced_vs_paid_last_3_months: [],
  },
  metrics: {
    amount_invoiced: 0,
    percent_change_invoiced_mom: null,
    amount_collected: 0,
    outstanding_amount: 0,
  },
  snapshot: {
    range: { start_date: '', end_date: '' },
    updated_at: null,
  },
};

/** GET b2b/billing/dashboard/outstanding-invoices */
export interface BillingOutstandingInvoiceApiItem {
  qbo_invoice_id: string;
  doc_number: string | null;
  customer_name: string;
  txn_date: string | null;
  due_date: string | null;
  total_amount: number;
  balance: number;
  days_overdue: number;
  currency: string;
  internal_invoice_id: string | null;
}

export interface BillingOutstandingInvoicesApiResponse {
  invoices: BillingOutstandingInvoiceApiItem[];
  refreshed_at: string | null;
  stale: boolean;
  error: boolean;
}

export const EMPTY_BILLING_OUTSTANDING_INVOICES_RESPONSE: BillingOutstandingInvoicesApiResponse = {
  invoices: [],
  refreshed_at: null,
  stale: false,
  error: false,
};